// GRO-404: the two context flags every account row and the sidebar carry -- a shared Slack
// channel and a past opportunity. Context only: nothing here feeds a sort, a score or a route.
//
// The server sends `has_shared_slack` and `past_opportunity` (book.py). Both are optional here
// because a payload cached before the deploy has neither, so the past opportunity falls back to a
// reading of `opportunities`, and the Slack flag to `pylon_slack_url`.

import type { BookAccount, PastOpportunity } from '../api/types';

export function hasSharedSlack(row: BookAccount): boolean {
  return Boolean(row.has_shared_slack || row.pylon_slack_url);
}

// Closed deals only: an open deal is current work rather than history.
export function pastOpportunityOf(row: BookAccount): PastOpportunity | null {
  if (row.past_opportunity) return row.past_opportunity;

  const closed = row.opportunities.filter(deal => Boolean(deal.closed));
  if (closed.length === 0) return null;

  // Newest close date first, falling back to the created date like the server does.
  const dayOf = (deal: { close_date: string | null; created: string | null }) =>
    deal.close_date || deal.created || '';
  const latest = closed.reduce((best, deal) => (dayOf(deal) > dayOf(best) ? deal : best));
  return {
    count: closed.length,
    stage: latest.stage,
    close_date: latest.close_date,
    owner: latest.owner ?? null,
  };
}

// 'Closed Lost · closed 2026-03-12 · owner Ana Reyes (3 closed, latest shown)'.
export function pastOpportunityDetail(past: PastOpportunity): string {
  const parts: string[] = [];
  if (past.stage) parts.push(past.stage);
  if (past.close_date) parts.push('closed ' + past.close_date);
  if (past.owner) parts.push('owner ' + past.owner);
  const text = parts.length ? parts.join(' · ') : 'Closed deal';
  return past.count > 1 ? `${text} (${past.count} closed, latest shown)` : text;
}

// The compact chip text: 'Past opp', or 'Past opp ×3' when there are several.
export function pastOpportunityChip(past: PastOpportunity): string {
  return past.count > 1 ? `Past opp ×${past.count}` : 'Past opp';
}

// Whether the past-opportunity line already says the account lost a deal. The sidebar header drew
// both "Prior closed-lost deal" and "Past opp ×3 · Closed Lost ..." for the same deal; when the
// latest closed deal is a loss, the past-opportunity line is the one kept.
export function pastOpportunityIsLoss(past: PastOpportunity | null): boolean {
  return Boolean(past?.stage && /lost/i.test(past.stage));
}
